import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories, featuredComicIds, hotComicIds, newUpdateIds } from '@/mocks/comics';

const featuredComics = featuredComicIds.map(id => comics.find(c => c.id === id)!).filter(Boolean);
const hotComics = hotComicIds.map(id => comics.find(c => c.id === id)!).filter(Boolean);
const newUpdates = newUpdateIds.map(id => comics.find(c => c.id === id)!).filter(Boolean);

const homepageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "韩漫画网",
      "url": "http://coffeemate.cn/",
      "description": "韩漫画网提供韩漫在线阅读服务，包含免费的韩国漫画和无删减下拉式韩漫大全",
      "sameAs": ["http://coffeemate.cn/", "http://coffeemate.cn/hanmanhua"],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "hello@coffeemate.cn",
        "availableLanguage": ["Chinese"]
      }
    },
    {
      "@type": "WebSite",
      "name": "韩漫画网",
      "url": "http://coffeemate.cn/",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "http://coffeemate.cn/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "WebPage",
      "name": "韩漫画网 - 首页",
      "url": "http://coffeemate.cn/",
      "description": "韩漫画网提供韩漫在线阅读服务，包含免费的韩国漫画和无删减下拉式韩漫大全",
      "isPartOf": { "@type": "WebSite", "name": "韩漫画网", "url": "http://coffeemate.cn/" }
    }
  ]
};

export default function Home() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchema) }} />
      <Navbar />

      <main>
        <section className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20with%20various%20comic%20characters%20in%20dynamic%20poses%2C%20colorful%20and%20vibrant%20art%20style%2C%20warm%20coral%20and%20teal%20accents%2C%20comic%20panel%20style%20background%20elements%2C%20artistic%20manhwa%20composition%2C%20energetic%20and%20engaging%20atmosphere%2C%20creative%20layout%20with%20speech%20bubbles%20and%20sound%20effects%2C%20modern%20webtoon%20aesthetic&width=1600&height=900&seq=hero-home-bg-01&orientation=landscape"
            alt="韩漫画网 - 韩漫在线阅读"
            title="韩漫画网 - 免费的韩国漫画阅读平台"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-background-50 font-heading leading-tight">
              探索最精彩的
              <br />
              韩国漫画世界 ✨
            </h1>
            <p className="mt-6 text-base md:text-lg text-background-200 max-w-2xl mx-auto leading-relaxed">
              韩漫画网提供海量韩漫在线阅读，涵盖恋爱、动作、奇幻、悬疑等多种类型
            </p>
            <p className="mt-2 text-sm md:text-base text-background-300 max-w-2xl mx-auto">
              无删减下拉式韩漫大全，内容完整无修改，原汁原味的韩漫阅读体验
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/all"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-all duration-300 whitespace-nowrap text-sm"
              >
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 text-background-200 hover:text-background-50 font-medium transition-colors duration-300 whitespace-nowrap text-sm"
              >
                了解更多 →
              </Link>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">浏览分类</h2>
                <p className="text-sm text-foreground-500 mt-1">选择您感兴趣的类型，发现更多精彩韩漫</p>
              </div>
              <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors">
                查看全部 →
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {categories.filter(c => c.slug !== 'all').map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-2 p-4 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors duration-300"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-lg text-primary-500`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                  <span className="text-xs text-foreground-400">{cat.count}部</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">精选推荐</h2>
                <p className="text-sm text-foreground-500 mt-1">编辑精选，不容错过的优质韩漫作品</p>
              </div>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} variant="featured" />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">🔥 热门排行</h2>
                <p className="text-sm text-foreground-500 mt-1">全网最受欢迎的韩漫作品，人气爆棚</p>
              </div>
              <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors">
                查看更多 →
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {hotComics.map((comic, idx) => (
                <div key={comic.id} className="relative">
                  <div className="absolute -top-1 -left-1 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                    {idx + 1}
                  </div>
                  <ComicCard comic={comic} variant="default" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">最新更新</h2>
                <p className="text-sm text-foreground-500 mt-1">每日更新，精彩不间断</p>
              </div>
              <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors">
                更多更新 →
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {newUpdates.map((comic) => (
                <div key={comic.id}>
                  <ComicCard comic={comic} variant="default" />
                  <p className="mt-2 text-xs text-foreground-500 text-center">
                    更新至 {comic.latestChapter}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-accent-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-background-50 font-heading">准备好开始阅读了吗？</h2>
            <p className="mt-4 text-base text-background-300 max-w-xl mx-auto">
              加入数百万韩漫读者，发现属于您的下一个最爱作品
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/all"
                className="px-8 py-3 bg-background-50 hover:bg-background-100 text-accent-900 font-semibold rounded-full transition-all whitespace-nowrap text-sm"
              >
                立即探索全部韩漫
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 border border-background-300/40 text-background-200 hover:text-background-50 hover:border-background-50 font-medium rounded-full transition-all whitespace-nowrap text-sm"
              >
                关于我们
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}